import { useState, useEffect, useRef } from 'react';
import { CopyButton } from './CopyButton';
import { safeHref } from '../utils/html';

interface EditableUrlProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSave?: (value: string) => void;
}

/**
 * Displays a URL as a clickable blue link with a pencil edit icon.
 * Clicking the icon (or the placeholder when empty) switches to an input.
 * Enter confirms; Escape cancels.
 */
export function EditableUrl({ label, value, onChange, onSave }: EditableUrlProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = () => { setDraft(value); setEditing(true); };
  const confirm = () => {
    onChange(draft);
    onSave?.(draft);
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {label && <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>{label}</span>}
      {editing ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={inputRef}
            type="url"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirm(); if (e.key === 'Escape') cancel(); }}
            style={{ flex: 1 }}
          />
          <button className="ghost-button" onClick={confirm} style={{ padding: '4px 10px', flexShrink: 0 }}>✓</button>
          <button className="ghost-button" onClick={cancel} style={{ padding: '4px 10px', flexShrink: 0 }}>✗</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {value ? (
            <a href={safeHref(value)} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.9rem' }}>{value}</a>
          ) : (
            <span className="muted-text" style={{ fontSize: '0.9rem' }}>—</span>
          )}
          {value && <CopyButton text={value} size={13} />}
          <button className="ghost-button" onClick={startEdit} style={{ padding: '4px 8px', flexShrink: 0 }} title="Edit URL">✏</button>
        </div>
      )}
    </div>
  );
}
